import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Download, X, Loader2 } from "lucide-react";
import { errorDetail } from "@/lib/utils";

export default function QrModal({ product, onClose }) {
  // Both qr.png and qr.pdf are now admin-only endpoints, so we can't use
  // `<img src=...>` or `<a href=...>` directly (the browser doesn't send our
  // Bearer token on those tag-driven requests). Fetch each asset through the
  // shared axios client, wrap the returned bytes in an object URL, and render
  // / trigger download from that. Always revoke the URLs on unmount so we
  // don't leak blobs.
  const [pngUrl, setPngUrl] = useState(null);
  const [pngError, setPngError] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const revokeRefs = useRef([]);

  useEffect(() => {
    let cancelled = false;
    if (!product?.id) return undefined;

    (async () => {
      try {
        const { data } = await api.get(`/products/${product.id}/qr.png`, {
          responseType: "blob",
        });
        if (cancelled) return;
        const url = URL.createObjectURL(data);
        revokeRefs.current.push(url);
        setPngUrl(url);
      } catch (e) {
        if (!cancelled) setPngError(errorDetail(e) || "Could not load QR");
      }
    })();

    return () => {
      cancelled = true;
      // Revoke every blob URL we created so no memory leaks per open/close cycle.
      for (const u of revokeRefs.current) {
        try {
          URL.revokeObjectURL(u);
        } catch {
          /* noop */
        }
      }
      revokeRefs.current = [];
    };
  }, [product?.id]);

  if (!product) return null;

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    revokeRefs.current.push(url);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleDownloadPng = async () => {
    // Reuse the already-fetched PNG blob if we have it; otherwise fetch fresh.
    try {
      const { data } = await api.get(`/products/${product.id}/qr.png`, {
        responseType: "blob",
      });
      downloadBlob(data, `HEDA-${product.code}-QR.png`);
    } catch (e) {
      alert(`Download failed: ${errorDetail(e) || e.message}`);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfBusy(true);
    try {
      const { data } = await api.get(`/products/${product.id}/qr.pdf`, {
        responseType: "blob",
      });
      downloadBlob(data, `HEDA-${product.code}-QR.pdf`);
    } catch (e) {
      alert(`Download failed: ${errorDetail(e) || e.message}`);
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="qr-modal-backdrop"
    >
      <div
        className="w-full max-w-md animate-fade-up overflow-hidden rounded-2xl border border-heda-line bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-heda-line px-5 py-3">
          <div className="min-w-0">
            <div className="hed-overline">Product QR · scan & learn</div>
            <h3 className="font-display text-lg font-bold text-heda-ink">{product.name}</h3>
            <div className="text-[11.5px] text-heda-mute">CODE · {product.code}</div>
          </div>
          <button
            onClick={onClose}
            className="rounded-full p-1.5 text-heda-mute hover:bg-heda-cream/60"
            data-testid="qr-modal-close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-5">
          <div className="mb-4 flex min-h-[288px] items-center justify-center rounded-2xl border border-heda-line bg-heda-cream/50 p-4 sm:p-6">
            {pngUrl ? (
              <img
                data-testid="qr-modal-image"
                src={pngUrl}
                alt="Product QR"
                className="h-64 w-64 object-contain"
              />
            ) : pngError ? (
              <div className="heda-error" data-testid="qr-modal-error">
                {pngError}
              </div>
            ) : (
              <div className="flex items-center gap-2 text-xs text-heda-mute" data-testid="qr-modal-loading">
                <Loader2 size={16} className="animate-spin text-heda-gold" /> Loading QR…
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              data-testid="qr-download-png"
              onClick={handleDownloadPng}
              disabled={!pngUrl}
              className="hed-btn-line whitespace-nowrap px-3"
            >
              <Download size={14} /> Download PNG
            </button>
            <button
              type="button"
              data-testid="qr-download-pdf"
              onClick={handleDownloadPdf}
              disabled={pdfBusy}
              className="heda-btn whitespace-nowrap px-3"
            >
              {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
              Branded PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
